import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RecruiterLayout from "@/components/layout/RecruiterLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, SlidersHorizontal, Bookmark, GitCompareArrows, User, Award, X } from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const rnd = (v) => (v == null ? null : Math.round(v * 100) / 100);
const EMPTY = { q: "", graduation_year: "", province: "", position: "", bats: "", throws: "", gpa_min: "", commitment_status: "", cbr_rating_min: "", has_awards: false, sort: "rating" };

export default function RecruiterSearch() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ items: [], total: 0 });
  const [opts, setOpts] = useState({ provinces: [], graduation_years: [], positions: POSITIONS });
  const [loading, setLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/recruiter/filters").then(({ data }) => setOpts(data)).catch(() => {});
    api.get("/recruiter/watchlists").then(({ data }) => setLists(data.items)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
    api.get("/recruiter/players", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Search failed"))
      .finally(() => setLoading(false));
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const addToList = async (wlId, playerId) => {
    try { await api.post(`/recruiter/watchlists/${wlId}/players`, { player_id: playerId }); toast.success("Added to watch list"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const toggleCompare = (id) => setCompareIds((c) => c.includes(id) ? c.filter((x) => x !== id) : (c.length >= 4 ? (toast.warning("Compare up to 4 players"), c) : [...c, id]));

  return (
    <RecruiterLayout>
      <div className="p-6 lg:p-8 max-w-7xl">
        <div className="label-overline text-[#E63946] flex items-center gap-2"><Search className="w-4 h-4" /> Discover Talent</div>
        <h1 data-testid="recruiter-search-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Search Players</h1>

        {/* Search bar */}
        <div className="mt-6 flex items-center gap-2 border border-slate-300 rounded-sm px-3 bg-white max-w-xl">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid="recruiter-search-input" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search by name, school, or team…" className="w-full py-3 text-sm outline-none" />
        </div>

        {/* Filters */}
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center gap-2 label-overline text-[10px] mb-3"><SlidersHorizontal className="w-4 h-4" /> Filters</div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Sel testid="filter-grad" label="Grad Year" value={filters.graduation_year} onChange={(v) => set("graduation_year", v)} options={opts.graduation_years} />
            <Sel testid="filter-province" label="Province" value={filters.province} onChange={(v) => set("province", v)} options={opts.provinces} />
            <Sel testid="filter-position" label="Position" value={filters.position} onChange={(v) => set("position", v)} options={[["PITCHER", "Pitcher (RHP + LHP)"], ...POSITIONS]} />
            <Sel testid="filter-bats" label="Bats" value={filters.bats} onChange={(v) => set("bats", v)} options={["L", "R", "S"]} />
            <Sel testid="filter-throws" label="Throws" value={filters.throws} onChange={(v) => set("throws", v)} options={["L", "R"]} />
            <Sel testid="filter-commit" label="Commitment" value={filters.commitment_status} onChange={(v) => set("commitment_status", v)} options={["uncommitted", "committed"]} />
            <Num testid="filter-gpa" label="Min GPA" value={filters.gpa_min} onChange={(v) => set("gpa_min", v)} step="0.1" />
            <Num testid="filter-rating" label="Min CBR" value={filters.cbr_rating_min} onChange={(v) => set("cbr_rating_min", v)} />
            <Sel testid="filter-sort" label="Sort" value={filters.sort} onChange={(v) => set("sort", v)} options={[["rating", "CBR Rating"], ["grad_year", "Grad Year"], ["name", "Name"], ["recent", "Newest"]]} noAll />
            <label className="flex items-center gap-2 text-sm mt-5"><input data-testid="filter-awards" type="checkbox" checked={filters.has_awards} onChange={(e) => set("has_awards", e.target.checked)} /> Has Awards</label>
            <button data-testid="filter-clear" onClick={() => setFilters(EMPTY)} className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#E63946] text-left">Clear</button>
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-4">{loading ? "Searching…" : `${data.total} player(s) found`}</p>

        {/* Results */}
        <div data-testid="recruiter-results" className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.map((p) => (
            <div key={p.id} data-testid={`player-card-${p.id}`} className="bg-white border border-slate-200 rounded-sm p-4 hover:border-[#E63946] transition-colors">
              <div className="flex items-start gap-3">
                <Link to={`/recruiter/players/${p.id}`} className="w-14 h-14 rounded-sm overflow-hidden bg-slate-100 shrink-0">
                  {p.profile_photo_url ? <img src={p.profile_photo_url} alt={p.first_name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><User className="w-6 h-6 text-slate-300" /></div>}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/recruiter/players/${p.id}`} className="font-display text-lg uppercase tracking-tight leading-tight hover:text-[#E63946]">{p.first_name} {p.last_name}</Link>
                  <div className="text-[11px] text-slate-500 mt-0.5">{[p.primary_position, p.graduation_year && `'${String(p.graduation_year).slice(-2)}`, p.province].filter(Boolean).join(" · ")}</div>
                  {p.complete_recruiting_profile && <span className="inline-flex items-center gap-1 mt-1 text-[9px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm"><Award className="w-2.5 h-2.5" /> Complete</span>}
                </div>
                {p.cbr_overall_rating != null && <div className="text-center shrink-0"><div className="font-data text-2xl text-[#0A192F]">{rnd(p.cbr_overall_rating)}</div><div className="label-overline text-[8px]">CBR</div></div>}
              </div>
              <div className="grid grid-cols-4 gap-1 mt-3 text-center">
                <Mini label="Nat'l" v={p.rankings?.national && `#${p.rankings.national}`} />
                <Mini label={p.province || "Prov"} v={p.rankings?.provincial && `#${p.rankings.provincial}`} />
                <Mini label="GPA" v={p.gpa} />
                <Mini label="B/T" v={(p.bats || "-") + "/" + (p.throws || "-")} />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <WatchlistPicker lists={lists} onPick={(wlId) => addToList(wlId, p.id)} onCreated={(nl) => setLists((l) => [nl, ...l])} />
                <button data-testid={`compare-toggle-${p.id}`} onClick={() => toggleCompare(p.id)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm border ${compareIds.includes(p.id) ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300 hover:bg-slate-50"}`}><GitCompareArrows className="w-3.5 h-3.5" /> {compareIds.includes(p.id) ? "Added" : "Compare"}</button>
              </div>
            </div>
          ))}
          {!loading && data.items.length === 0 && <div className="col-span-full text-center py-16 text-slate-400"><Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />No players match these filters.</div>}
        </div>
      </div>

      {/* Compare tray */}
      {compareIds.length > 0 && (
        <div data-testid="compare-tray" className="fixed bottom-0 left-0 lg:left-64 right-0 bg-[#0A192F] text-white px-6 py-3 pr-6 lg:pr-48 flex items-center justify-between z-[60]">
          <span className="text-sm">{compareIds.length} selected for comparison</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCompareIds([])} className="text-xs uppercase tracking-wider text-white/60 hover:text-white flex items-center gap-1"><X className="w-4 h-4" /> Clear</button>
            <Link data-testid="go-compare" to={`/recruiter/compare?ids=${compareIds.join(",")}`} className={`bg-[#E63946] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${compareIds.length < 2 ? "opacity-50 pointer-events-none" : ""}`}>Compare Now</Link>
          </div>
        </div>
      )}
    </RecruiterLayout>
  );
}

function Sel({ label, value, onChange, options, testid, noAll }) {
  return (
    <label className="flex flex-col gap-1"><span className="label-overline text-[9px]">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm bg-white">
        {!noAll && <option value="">All</option>}
        {(options || []).map((o) => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Num({ label, value, onChange, testid, step }) {
  return <label className="flex flex-col gap-1"><span className="label-overline text-[9px]">{label}</span><input data-testid={testid} type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-2 py-2 text-sm" /></label>;
}
function Mini({ label, v }) {
  return <div className="bg-slate-50 rounded-sm py-1"><div className="font-data text-sm text-[#0A192F]">{v || "—"}</div><div className="label-overline text-[8px] text-slate-400">{label}</div></div>;
}
function WatchlistPicker({ lists, onPick, onCreated }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    try { const { data } = await api.post("/recruiter/watchlists", { name }); onCreated(data); onPick(data.id); setName(""); setCreating(false); setOpen(false); }
    catch { toast.error("Could not create list"); }
  };
  return (
    <div className="relative">
      <button data-testid="add-watchlist-btn" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"><Bookmark className="w-3.5 h-3.5" /> Save</button>
      {open && (
        <div className="absolute z-20 mt-1 w-52 bg-white border border-slate-200 rounded-sm shadow-lg p-1" onMouseLeave={() => setOpen(false)}>
          {lists.map((l) => <button key={l.id} data-testid={`wl-option-${l.id}`} onClick={() => { onPick(l.id); setOpen(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 rounded-sm">{l.name}</button>)}
          {!creating ? (
            <button onClick={() => setCreating(true)} className="w-full text-left px-3 py-2 text-xs text-[#E63946] font-semibold hover:bg-slate-50 rounded-sm">+ New List</button>
          ) : (
            <div className="p-1 flex gap-1"><input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="List name" className="flex-1 border border-slate-300 rounded-sm px-2 py-1 text-xs" /><button onClick={create} className="bg-[#E63946] text-white px-2 rounded-sm text-xs">Add</button></div>
          )}
        </div>
      )}
    </div>
  );
}
